import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useCreateBusinessAccount } from "@/api/use-customer-business-account-api";
import type { AxiosError } from "axios";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { HeroInputField } from "@/components/hero-form";

type BusinessAccountFormValues = {
  companyName: string;
  taxNumber?: string;
};

interface CreateBusinessAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateBusinessAccountDialog({
  open,
  onOpenChange,
}: CreateBusinessAccountDialogProps) {
  const { t } = useTranslation("app");
  const { toast } = useToast();
  const businessAccountSchema = z.object({
    companyName: z.string().min(1, t("validation.companyNameRequired")),
    taxNumber: z.string().optional(),
  });

  const form = useForm<BusinessAccountFormValues>({
    resolver: zodResolver(businessAccountSchema),
    defaultValues: {
      companyName: "",
      taxNumber: "",
    },
  });

  const createBusinessAccountMutation = useCreateBusinessAccount({
    onSuccess: () => {
      onOpenChange(false);
      toast({
        title: t("businessAccountScreen.toast.success"),
        description: t("businessAccountScreen.toast.created"),
      });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast({
        title: t("businessAccountScreen.toast.error"),
        description:
          error.response?.data?.message ??
          t("businessAccountScreen.toast.createFailed"),
        variant: "destructive",
      });
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    createBusinessAccountMutation.mutate(values);
  });

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      placement="center"
      backdrop="blur"
      disableAnimation
      size="md"
    >
      <ModalContent>
        {(onClose) => (
          <form onSubmit={onSubmit}>
            <ModalHeader className="flex-col items-start gap-1">
              <span>{t("businessAccountScreen.createTitle")}</span>
              <span className="text-small text-default-500 font-normal">
                {t("businessAccountScreen.dialogDescription")}
              </span>
            </ModalHeader>
            <ModalBody className="gap-4">
              <HeroInputField
                control={form.control}
                name="companyName"
                label={t("settings.companyName")}
                variant="flat"
              />
              <HeroInputField
                control={form.control}
                name="taxNumber"
                label={t("settings.taxNumber")}
                variant="flat"
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="light" type="button" onPress={onClose}>
                {t("actions.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                isDisabled={createBusinessAccountMutation.isPending}
                isLoading={createBusinessAccountMutation.isPending}
              >
                {createBusinessAccountMutation.isPending
                  ? t("actions.creating")
                  : t("businessAccountScreen.createAccount")}
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
}
